import { useState, useEffect, useCallback } from 'react';
import { NavLink, generatePath } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  InputText,
  Select,
  formatDate,
  Checkbox,
  IconRight,
  IconAttention,
  IconLoader,
  Popup,
  IconTrash,
  ButtonPrimary,
  ButtonSize,
  IconSelected,
  IconChangeLoClock,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector } from '@store';
import { Company, CofProps, CofStatus } from '../../../hoc/customers/types';
import {
  useUpdateCorporateCOFMutation,
  useUpdateCofStatusMutation,
} from '../../../hoc/corporate/store/services/corporate';
import { useLazyGetCustomersEmployeeQuery } from '../../../store/dictionaries/services';
import { AccessRight, SignatureType } from '../../../hoc/corporate/types';
import { NatureOfControlEnum } from '../../../hoc/new-application/types';
import { getCofStatus } from '../../corporate/overview/helpers';
import { CompanyItemTypes } from '../types';

import ROUTES from '../../../routes';

import cn from './overview-content.module.css';

const CompanyItem = ({ value, removed, list }: CompanyItemTypes) => {
  const [formValues, setFormValues] = useState<Company>(value as Company);
  const [defaultFormValues, setDefaultFormValues] = useState<
    Company | undefined
  >(undefined);
  const [rejectReason, setRejectReason] = useState('');
  const [employeeName, setEmployeeName] = useState('');

  const [popupIsVisible, setPopupIsVisible] = useState(false);

  const [updateCof, { isLoading: cofIsUpdate }] =
    useUpdateCorporateCOFMutation();
  const [
    updateCofStatus,
    { isLoading: updateStatusInProgress, isSuccess: updateStatusSuccess },
  ] = useUpdateCofStatusMutation();
  const [approveCof, { isLoading: approveInProgress }] =
    useUpdateCofStatusMutation();
  const [getCustomersEmployee] = useLazyGetCustomersEmployeeQuery();

  const getCustomersEmployeeAction = useCallback(async (id: string) => {
    const response = await getCustomersEmployee(id);

    if (response.data?.data) {
      setEmployeeName(
        `${response.data.data?.profile?.firstName} ${response.data.data?.profile?.lastName}`,
      );
    }
  }, []);

  const { activePermissions } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const updateCofAction = useCallback(async () => {
    const body: {
      position: string;
      percentage?: string;
      natureOfControl?: NatureOfControlEnum;
      companyOfficialType: string;
      signatureType: SignatureType;
      accessRight: string;
    } = {
      position: formValues.position,
      companyOfficialType: value?.companyOfficialType as string,
      signatureType: formValues.signatureType,
      accessRight: formValues.accessRight as string,
    };

    if (formValues.havingControl) {
      body.percentage = formValues.percentage;
      body.natureOfControl = formValues.natureOfControl;
    } else if (!formValues.havingControl && value?.percentage) {
      body.percentage = '';
      body.natureOfControl = undefined;
    }

    const response = (await updateCof({
      id: value?.id as string,
      body,
    })) as { data: { data: CofProps } };

    if (response?.data?.data) {
      setDefaultFormValues({
        ...formValues,
        havingControl: Boolean(response.data.data.percentage),
      });
    }

    return response;
  }, [formValues]);

  const onUpdateCofStatus = (status: CofStatus) => {
    const body: {
      status: CofStatus;
      rejectionReason?: string;
    } = {
      status,
    };

    if (rejectReason) {
      body.rejectionReason = rejectReason;
    }

    updateCofStatus({
      id: value?.id as string,
      body,
    });
  };

  const onApprove = async () => {
    let approveIsNotAllowed = false;

    if (!isEqual(formValues, defaultFormValues)) {
      approveIsNotAllowed = true;

      const updateCofResponse = await updateCofAction();

      if (updateCofResponse && updateCofResponse.data) {
        approveIsNotAllowed = false;
      }
    }

    if (!approveIsNotAllowed) {
      const response = (await approveCof({
        id: value?.id as string,
        body: {
          status: CofStatus.APPROVED,
        },
      })) as { data: { data: CofProps } };

      if (response?.data?.data) {
        if (value?.companyOfficialType === 'MAIN_REPRESENTATIVE') {
          if (
            list
              ?.filter((item) => item.id !== value.id)
              .find(
                (item) => item.companyOfficialType === 'MAIN_REPRESENTATIVE',
              )
          ) {
            const prevMainRepresentative = list
              ?.filter(
                (item) =>
                  item.id !== value.id && item.status === CofStatus.APPROVED,
              )
              .find(
                (item) => item.companyOfficialType === 'MAIN_REPRESENTATIVE',
              );

            if (prevMainRepresentative) {
              updateCofStatus({
                id: prevMainRepresentative?.id,
                body: {
                  status: CofStatus.ARCHIVED,
                  rejectionReason:
                    'Another user approved as Main Representative',
                },
              });
            }
          }
        }

        setDefaultFormValues({
          ...formValues,
          havingControl: Boolean(response.data.data.percentage),
        });
      }
    }
  };

  useEffect(() => {
    if (value) {
      const nextFormValues = { ...value };

      nextFormValues.havingControl = Boolean(value.percentage);
      nextFormValues.accessRight = value.accessRight || AccessRight.NONE;

      setFormValues(nextFormValues);

      if (!defaultFormValues) {
        setDefaultFormValues(nextFormValues);
      }
    }
  }, [value, defaultFormValues]);

  useEffect(() => {
    if (value) {
      if (removed) {
        getCustomersEmployeeAction(value.archivedBy || value.rejectedBy);
      } else if (!removed && value.approvedBy) {
        getCustomersEmployeeAction(value.approvedBy);
      }
    }
  }, [value, removed]);

  useEffect(() => {
    if (updateStatusSuccess) {
      setPopupIsVisible(false);
      setRejectReason('');
    }
  }, [updateStatusSuccess]);

  let type;

  if (formValues.companyOfficialType === 'MAIN_REPRESENTATIVE') {
    type = 'Main Representative';
  } else if (formValues.companyOfficialType === 'COMPANY_OFFICIAL') {
    type = 'Company Official';
  }

  const { cofStatus, cofStatusClassName } = getCofStatus(
    value?.status as CofStatus,
  );

  let rejectOkButtonText;
  let approveButtonText = '';

  if (updateStatusInProgress) {
    rejectOkButtonText = 'Loading...';
  } else if (
    !updateStatusInProgress &&
    value?.status === CofStatus.PENDING_REVIEW
  ) {
    rejectOkButtonText = 'Reject';
  } else {
    rejectOkButtonText = 'Archive';
  }

  if (value?.companyOfficialType === 'MAIN_REPRESENTATIVE') {
    approveButtonText = 'Main Representative';
  } else if (value?.companyOfficialType === 'COMPANY_OFFICIAL') {
    approveButtonText = 'COF';
  }

  const link = generatePath(ROUTES.CUSTOMERS_CORPORATE_PAGE, {
    customerId: (value?.organization.id as string) || '',
  });

  return (
    <div
      className={`${cn.addCompanyOfficialInputsWrapper} ${cn.addCompanyOfficialItemWrapper}`}
    >
      <div className={cn.companyOfficialTop}>
        <NavLink to={link} target="_blank" className={cn.companyOfficialName}>
          {value?.organization.companyName}

          <IconRight />
        </NavLink>
        <div className={cn.companyOfficialAcceptedDate}>
          {`${
            value?.approvedAt || value?.status === CofStatus.APPROVED
              ? 'Approved'
              : 'Appointed'
          } in ${formatDate(
            formValues.approvedAt
              ? formValues.approvedAt
              : formValues.createdAt,
          )}`}
          {!removed && employeeName && (
            <div className={cn.approvedBy}>{`Approved by ${employeeName}`}</div>
          )}
        </div>
      </div>
      <div className={cn.companyOfficialItemStatus}>
        <div className={cn.companyOfficialItemStatus}>
          {`${type} status:`}
          <span className={cofStatusClassName}>{cofStatus}</span>
        </div>
        {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
          <NavLink
            target="_blank"
            to={`/administration/change-logs?entityType=ORGANIZATION&entityId=${value?.organization.id}`}
            className={cn.cofChangeLogLink}
          >
            <IconChangeLoClock />
            Change Log
          </NavLink>
        )}
      </div>
      {value?.status === CofStatus.PENDING_REVIEW && (
        <div className={cn.approveCofWrapper}>
          <ButtonPrimary
            size={ButtonSize.SMALL}
            text={
              approveInProgress ||
              (!isEqual(formValues, defaultFormValues) && cofIsUpdate)
                ? 'Loading...'
                : `Approve ${approveButtonText}`
            }
            icon={
              approveInProgress ||
              (!isEqual(formValues, defaultFormValues) &&
                cofIsUpdate) ? undefined : (
                <IconSelected />
              )
            }
            disabled={
              approveInProgress ||
              (!isEqual(formValues, defaultFormValues) && cofIsUpdate)
            }
            onClick={onApprove}
          />
          <div className={cn.approveCofText}>
            The employee is awaiting confirmation of Company Official status
          </div>
        </div>
      )}
      {removed && (
        <div className={cn.removedCompanyOfficialBlock}>
          <div className={cn.removedLeft}>
            <IconAttention />
            {`${
              value?.status === CofStatus.REJECTED ? 'Rejected' : 'Archived'
            } from the company in ${formatDate(
              value?.status === CofStatus.REJECTED
                ? value.rejectedAt
                : (value?.archivedAt as string),
            )} ${employeeName ? `by ${employeeName}` : ''}`}
          </div>
          {value?.rejectionReason && (
            <div className={cn.rejectionReason}>{`${
              value?.status === CofStatus.REJECTED ? 'Rejection' : 'Archive'
            } reason: ${value.rejectionReason}`}</div>
          )}
        </div>
      )}

      <div className={cn.companyOfficialValueWrapper}>
        <InputText
          name="position"
          value={formValues.position}
          title="Position"
          placeholder="Enter position"
          onChange={(e) => {
            setFormValues({ ...formValues, position: e.target.value });
          }}
          disabled={removed}
        />
      </div>
      <div className={cn.companyOfficialValueWrapper}>
        <Checkbox
          type="checkbox"
          name={`havingControl-${value?.id}`}
          checked={Boolean(formValues.havingControl)}
          title="Having a control in company"
          onChange={(e) => {
            setFormValues({ ...formValues, havingControl: e.target.checked });
          }}
          disabled={removed}
        />
      </div>
      {formValues.havingControl && (
        <div
          className={`${cn.companyOfficialValueWrapper} ${cn.companyOfficialValueWrapperFlex}`}
        >
          <div className={cn.inputFlex}>
            <InputText
              name="percentageOfControl"
              value={formValues.percentage}
              title="Percentage of control"
              placeholder="Enter Percentage of control"
              onChange={(e) => {
                setFormValues({
                  ...formValues,
                  percentage: e.target.value,
                });
              }}
              disabled={removed}
            />
          </div>
          <div className={cn.inputFlex}>
            <Select
              list={[
                {
                  name: 'Direct Control',
                  value: NatureOfControlEnum.DIRECT_CONTROL,
                },
                {
                  name: 'Indirect Control',
                  value: NatureOfControlEnum.INDIRECT_CONTROL,
                },
              ]}
              name="natureOfControl"
              value={formValues.natureOfControl}
              title="Nature of control"
              placeholder="Select Nature of control"
              onChange={(e) => {
                setFormValues({
                  ...formValues,
                  natureOfControl: e.target.value as NatureOfControlEnum,
                });
              }}
              disabled={removed}
            />
          </div>
        </div>
      )}
      <div className={cn.companyOfficialValueWrapper}>
        <Select
          list={[
            {
              name: 'None',
              value: SignatureType.NONE,
            },
            {
              name: 'Single',
              value: SignatureType.SINGLE,
            },
          ]}
          name="signatureType"
          value={formValues.signatureType}
          title="Signature Type"
          onChange={(e) => {
            setFormValues({
              ...formValues,
              signatureType: e.target.value as SignatureType,
            });
          }}
          fullWidth
          disabled={removed}
        />
      </div>
      <div className={cn.companyOfficialValueWrapper}>
        <Select
          list={[
            {
              name: 'None',
              value: AccessRight.NONE,
            },
            {
              name: 'Viewer',
              value: AccessRight.VIEWER,
            },
          ]}
          name="accessRight"
          value={formValues.accessRight || AccessRight.VIEWER}
          onChange={(e) => {
            setFormValues({
              ...formValues,
              accessRight: e.target.value as SignatureType,
            });
          }}
          title="Access Right"
          disabled={removed}
          fullWidth
        />
      </div>
      {!removed && (
        <div className={cn.cofControls}>
          <button
            type="button"
            className={`${cn.companyOfficialSubmit} ${
              cofIsUpdate ? cn.cofUpdate : ''
            }`}
            onClick={updateCofAction}
            disabled={isEqual(formValues, defaultFormValues)}
          >
            {cofIsUpdate ? <IconLoader width={20} height={20} /> : 'Save'}
          </button>
          <button
            type="button"
            className={cn.companyOfficialDeleteButton}
            onClick={() => setPopupIsVisible(true)}
          >
            <IconTrash />
            {value?.status === CofStatus.PENDING_REVIEW ? 'Reject' : 'Archive'}
          </button>
        </div>
      )}
      <Popup
        parent={document.getElementById('popup-modal') as HTMLElement}
        title={`Remove from ${value?.organization.companyName}?`}
        description={`${formValues?.user?.profile?.firstName} ${
          formValues?.user?.profile?.lastName
        } will be ${
          value?.status === CofStatus.PENDING_REVIEW ? 'rejected' : 'archived'
        } from the ${
          value?.organization.companyName
        }. But he will remain on the list of deleted official.`}
        onCancelClick={() => {
          setPopupIsVisible(false);
          setRejectReason('');
        }}
        onOkClick={() =>
          onUpdateCofStatus(
            value?.status === CofStatus.PENDING_REVIEW
              ? CofStatus.REJECTED
              : CofStatus.ARCHIVED,
          )
        }
        cancelButtonText="Cancel"
        okButtonText={rejectOkButtonText}
        isVisible={popupIsVisible}
        isLoading={updateStatusInProgress}
      >
        <div>
          {value?.status === CofStatus.PENDING_REVIEW && (
            <div className={cn.rejetReasonInput}>
              <InputText
                name="rejectReason"
                title="Reject reason"
                placeholder="Reject reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </div>
          )}
        </div>
      </Popup>
    </div>
  );
};

export default CompanyItem;
